import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import GenericPage from '../../components/GenericPage';
import {
    fetchStockadjustments,
    createStockAdjustment,
    updateStockAdjustment,
    deleteStockAdjustment,
    setPagination,
    setSort,
    searchStockadjustments,
    setDefaultFilter,
    changeCondition,
    addNewCondition,
    updateOptionRule,
    updateOptionValue,
    removeCondition,
    saveFilterCondition,
    setSelected,
    resetFiltersAndPagination,
    changeClassicFilter,
    setVisibleColumns,
    applyStockAdjustmentListFilters,
    setStockAdjustmentSortAndFetch,
    setClassicFilterDateRange,
} from '../../store/modules/stockadjustment/stockadjustmentActions';
import StockAdjustmentForm from '../../components/pageModule/Stockadjustments/StockAdjustmentForm';
import StockAdjustmentView from '../../components/pageModule/Stockadjustments/StockAdjustmentView';
import StockAdjustmentListToolbar from '../../components/pageModule/Stockadjustments/StockAdjustmentListToolbar';
import { useHasPermission } from '../../utils/permissionUtils';

const MySwal = withReactContent(Swal);

const Stockadjustments = ({
    excludedActions = [],
    onSelectedRowsChange = () => { },
    onClickedRow = () => { },
    externalCondition = {},
    selectedIds = []
}) => {

    const hasCreatePermission = useHasPermission(['create stock adjustments']);
    const hasViewPermission = useHasPermission(['view stock adjustments']);

    const stockadjustmentState = useSelector(state => state.stockadjustment);
    const dispatch = useDispatch();

    const handleCreate = async (formData) => {
        try {
            const response = await dispatch(createStockAdjustment(formData));
            toast.success('Stock adjustment created successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error creating stock adjustment', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleUpdate = async (id, formData) => {
        try {
            const response = await dispatch(updateStockAdjustment(id, formData));
            toast.success('Stock adjustment updated successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error updating stock adjustment', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleDelete = async (id) => {
        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteStockAdjustment(id));
                toast.success('Stock adjustment deleted successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return true;
            } catch (error) {
                toast.error(error.message || 'Error deleting stock adjustment', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return false;
            }
        }
        return false;
    };

    const allColumns = useMemo(() => [
        {
            id: 'adjustment_number',
            name: 'Adjustment Number',
            selector: row => row.adjustment_number,
            minWidth: '170px'
        },
        {
            id: 'adjustment_date',
            name: 'Adjustment Date',
            selector: row => row.adjustment_date,
            sortable: true,
            sortField: 'stock_adjustments.adjustment_date',
            minWidth: '120px'
        },
        {
            id: 'location',
            name: 'Location',
            selector: row => row.location_name,
            minWidth: '150px'
        },
        {
            id: 'item',
            name: 'Item',
            selector: row => (row.item_code ? `${row.item_code} - ${row.item_name}` : row.item_name),
            grow: 2,
        },
        {
            id: 'quantity',
            name: 'Quantity',
            selector: row => {
                const qty = parseFloat(row.quantity);
                const color = qty > 0 ? 'text-success' : 'text-danger';
                const prefix = qty > 0 ? '+' : '';
                return <span className={`fw-bold ${color}`}>{prefix}{qty.toFixed(3)}</span>;
            },
            minWidth: '100px'
        },
        {
            id: 'reason',
            name: 'Reason',
            selector: row => row.reason_label || row.reason || '-',
            minWidth: '130px'
        },
        {
            id: 'adjusted_by',
            name: 'Created By',
            selector: row => {
                if (row.created_by_name && row.created_by_email) {
                    return `${row.created_by_name} (${row.created_by_email})`;
                }
                return row.created_by_name || '-';
            },
            minWidth: '140px'
        },
        {
            id: 'unit_cost',
            name: 'Unit Cost',
            selector: row => row.unit_cost != null ? parseFloat(row.unit_cost).toFixed(3) : '-',
            minWidth: '100px'
        },
        {
            id: 'line_amount',
            name: 'Line Amount',
            selector: row => {
                if (row.line_amount == null) {
                    return '-';
                }
                const amount = parseFloat(row.line_amount);
                const color = amount >= 0 ? 'text-success' : 'text-danger';
                const prefix = amount > 0 ? '+' : '';
                return <span className={`fw-semibold ${color}`}>{prefix}{amount.toFixed(3)}</span>;
            },
            minWidth: '110px'
        },
        {
            id: 'created_at',
            name: 'Created At',
            selector: row => row.created_at || '-',
            minWidth: '150px'
        },
        {
            id: 'notes',
            name: 'Notes',
            selector: row => row.notes || '-',
            grow: 2,
            minWidth: '180px'
        },
    ], []);

    const columns = useMemo(() => {
        const visibleIds = stockadjustmentState.visibleColumns || [];
        return allColumns.filter((column) => visibleIds.includes(column.id));
    }, [allColumns, stockadjustmentState.visibleColumns]);

    const handleToolbarSearch = useCallback((value) => {
        dispatch(searchStockadjustments(value));
    }, [dispatch]);

    const handleToolbarApplyFilters = useCallback((params = {}) => {
        dispatch(applyStockAdjustmentListFilters(params));
    }, [dispatch]);

    const handleLocationChange = useCallback(async (value) => {
        if (!value) {
            dispatch(changeClassicFilter('location_id', { value: '', label: '' }));
            return;
        }
        dispatch(changeClassicFilter('location_id', { value: value.id, label: value.label }));
    }, [dispatch]);

    const handleDateRangeChange = useCallback(async (fromDate, toDate) => {
        dispatch(setClassicFilterDateRange(fromDate, toDate));
    }, [dispatch]);

    const handleClearDates = useCallback(async () => {
        dispatch(setClassicFilterDateRange('', ''));
    }, [dispatch]);

    const handleSortChange = useCallback(async (option) => {
        await dispatch(setStockAdjustmentSortAndFetch({
            field: option.field,
            direction: option.direction,
        }));
    }, [dispatch]);

    const handleToggleColumns = useCallback((nextColumns) => {
        dispatch(setVisibleColumns(nextColumns));
    }, [dispatch]);

    const actions = {
        fetch: (queryFilter, activeFilter, shouldUseFilter, externalCondition) => dispatch(fetchStockadjustments(queryFilter, activeFilter, shouldUseFilter, externalCondition)),
        create: handleCreate,
        update: handleUpdate,
        delete: handleDelete,
        search: (search) => dispatch(searchStockadjustments(search)),
        setPagination: (pagination) => dispatch(setPagination(pagination)),
        setSort: (sort) => dispatch(setSort(sort)),
        setDefaultFilter: (filter) => dispatch(setDefaultFilter(filter)),
        changeCondition: (index, condition) => dispatch(changeCondition(index, condition)),
        addNewCondition: (rule) => dispatch(addNewCondition(rule)),
        updateOptionRule: (index, rule) => dispatch(updateOptionRule(index, rule)),
        updateOptionValue: (index, value) => dispatch(updateOptionValue(index, value)),
        removeCondition: (index) => dispatch(removeCondition(index)),
        saveFilterCondition: (filter) => dispatch(saveFilterCondition(filter)),
        setSelected: (selectedRows) => dispatch(setSelected(selectedRows)),
        onClassicFilterChange: (name, value) => dispatch(changeClassicFilter(name, value)),
    };

    let defaultAvailableActions = [];
    if (hasCreatePermission) {
        defaultAvailableActions = ['create', ...defaultAvailableActions];
    }
    if (hasViewPermission) {
        defaultAvailableActions = ['actions', 'view', ...defaultAvailableActions];
    }
    const availableActions = defaultAvailableActions.filter(action => !excludedActions.includes(action));

    useEffect(() => {
        return () => {
            dispatch(resetFiltersAndPagination());
        };
    }, [dispatch]);

    return (
        <>
            <GenericPage
                title="Stock Adjustments"
                moduleName="StockAdjustment"
                moduleKey="stockadjustments"
                state={stockadjustmentState}
                actions={actions}
                FormComponent={StockAdjustmentForm}
                ViewComponent={StockAdjustmentView}
                columns={columns}
                availableActions={availableActions}
                actionsColumnWidth="150px"
                urlKey="stock_adjustments"
                actionType="sidebar"
                onSelectedRowsChange={onSelectedRowsChange}
                selectedIds={selectedIds}
                externalCondition={externalCondition}
                hideClassicFilter
                tableToolbar={() => (
                    <StockAdjustmentListToolbar
                        state={stockadjustmentState}
                        onSearch={handleToolbarSearch}
                        onLocationChange={handleLocationChange}
                        onDateRangeChange={handleDateRangeChange}
                        onClearDates={handleClearDates}
                        onSortChange={handleSortChange}
                        onToggleColumn={handleToggleColumns}
                        onApplyFilters={handleToolbarApplyFilters}
                    />
                )}
            />
        </>
    );
};

export default Stockadjustments;
